import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingBag, Package } from 'lucide-react';

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '');
const API = `${BACKEND_BASE}/api`;

const ALLOWED_CATEGORIES = ['Paddles', 'Balls', 'Footwear', 'Accessories', 'Equipment'];

// Helper function to format currency
const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const EquipmentPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const params = categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
        const response = await fetch(`${API}/equipment${params}`);
        if (response.ok) {
          const data = await response.json();
          setEquipment(data.filter(item => item.is_active !== false));
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEquipment();
  }, [categoryFilter]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="equipment-page">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2 text-foreground">Equipment Rental</h1>
            <p className="text-muted-foreground">Rent quality equipment for your game</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {ALLOWED_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Equipment Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-64 bg-muted animate-pulse rounded-xl"></div>)}
          </div>
        ) : equipment.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipment.map((item) => (
              <Card
                key={item.item_id || item.equipment_id}
                className="card-hover overflow-hidden bg-white text-gray-900 border border-orange-100 shadow-sm hover:shadow-md"
                data-testid={`equipment-${item.item_id || item.equipment_id}`}
              >
                <div className="h-40 bg-slate-100 flex items-center justify-center">
                  <img 
                    src={item.image || 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'; }}
                  />
                </div>
                <CardContent className="p-5">
                  {item.category && (
                    <Badge variant="outline" className="mb-2 bg-orange-50 text-orange-800 border-orange-200">{item.category}</Badge>
                  )}
                  <h3 className="font-heading font-semibold text-lg mb-1 text-gray-900">{item.name}</h3>
                  {item.description && (
                    <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-[#d26a00]">{formatCurrency(item.price || item.price_per_hour || 0)}</p>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={item.available_quantity > 5 ? 'default' : 'destructive'}
                        className={item.available_quantity > 5 ? 'bg-green-100 text-green-800' : 'bg-red-50 text-red-800'}
                      >
                        {(item.available_quantity ?? 0)} available
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2 text-gray-900">No equipment available</h3>
            <p className="text-gray-700">Check back later</p>
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-8 bg-gradient-to-r from-orange-50 via-white to-orange-50 border border-orange-100">
          <CardContent className="p-6 text-center text-gray-800">
            <ShoppingBag className="h-8 w-8 mx-auto text-[#d26a00] mb-3" />
            <h3 className="font-heading font-semibold mb-2">How to Rent</h3>
            <p className="text-sm text-gray-700">
              Select equipment during the booking process. Equipment will be ready for pickup at the venue.
            </p>
          </CardContent>
        </Card>
      </div>

    </Layout>
  );
};

export default EquipmentPage;
